import { useCallback, useEffect, useRef, useState } from "react";
import { TOKEN_KEY } from "../lib/api";

const WS_BASE = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/^http/, "ws");

export const useLiveSession = (sessionId, initial) => {
  const [status, setStatus] = useState("idle"); // idle | connecting | listening | disconnected | ended
  const [interims, setInterims] = useState({});
  const interim = Object.values(interims).join(" ");
  const [segments, setSegments] = useState(initial?.transcript || []);
  const [suggestions, setSuggestions] = useState(initial?.suggestions || []);
  const [pendingTrigger, setPendingTrigger] = useState(null);
  const [draft, setDraft] = useState(null);
  const [coachState, setCoachState] = useState("listening");
  const suggestionIdRef = useRef(null);
  const [sellerSpeaker, setSellerSpeaker] = useState(initial?.seller_speaker ?? null);
  const [sellerAuto, setSellerAuto] = useState(null);
  const [voiceProfile, setVoiceProfile] = useState(false);
  const [error, setError] = useState("");
  const [level, setLevel] = useState(0);
  const [sources, setSources] = useState({ tab: false, mic: false });
  const [channelLevels, setChannelLevels] = useState({ mic: 0, tab: 0 });
  const [channels, setChannels] = useState(1);
  const wsRef = useRef(null);
  const ctxRef = useRef(null);
  const tabStreamRef = useRef(null);
  const micStreamRef = useRef(null);
  const nodeRef = useRef(null);
  const stoppingRef = useRef(false);
  const channelsRef = useRef(1);

  const openSocket = useCallback(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    const ws = new WebSocket(`${WS_BASE}/api/live/ws/${sessionId}?token=${token}&channels=${channelsRef.current}`);
    ws.binaryType = "arraybuffer";
    setStatus("connecting");
    setCoachState("listening");
    ws.onopen = () => setError((e) => (e.startsWith("Errore di connessione") ? "" : e));
    ws.onmessage = (e) => {
      if (wsRef.current !== ws) return;
      const d = JSON.parse(e.data);
      if (d.type === "status") {
        setStatus(d.state === "listening" ? "listening" : "disconnected");
        if (d.seller_speaker !== undefined) setSellerSpeaker(d.seller_speaker);
        if (d.auto !== undefined) setSellerAuto(d.auto ? { confidenza: d.confidenza } : null);
        if (d.voice_profile !== undefined) setVoiceProfile(d.voice_profile);
        if (d.message) setError(d.message);
      } else if (d.type === "transcript") {
        setInterims((s) => ({ ...s, [d.channel]: d.is_final ? "" : d.testo }));
      } else if (d.type === "segment") {
        setInterims((s) => ({ ...s, [d.channel]: "" }));
        setSegments((s) => [...s, d]);
      } else if (d.type === "coach_state") {
        setCoachState(d.state);
        if (d.state !== "ready") setDraft(null);
      } else if (d.type === "suggestion_pending") {
        setCoachState("listening");
        suggestionIdRef.current = d.id;
        setPendingTrigger(d.trigger_text);
        setDraft(null);
      } else if (d.type === "suggestion_partial" && d.id === suggestionIdRef.current) {
        setDraft(d);
      } else if (d.type === "suggestion" && d.id === suggestionIdRef.current) {
        setPendingTrigger(null);
        setDraft(null);
        setSuggestions((s) => [...s, d]);
      } else if (d.type === "suggestion_error" && d.id === suggestionIdRef.current) {
        setPendingTrigger(null);
        setDraft(null);
        setError(d.message);
      } else if (d.type === "error") {
        setError(d.message);
      }
    };
    ws.onclose = () => {
      if (wsRef.current !== ws) return;
      suggestionIdRef.current = null;
      setPendingTrigger(null);
      setDraft(null);
      setInterims({});
      if (!stoppingRef.current) setStatus("disconnected");
    };
    ws.onerror = () => setError("Errore di connessione al server live");
    wsRef.current = ws;
    return ws;
  }, [sessionId]);

  const start = useCallback(async (mode = "auto") => {
    setError("");
    setSources({ tab: false, mic: false });
    const warnings = [];
    let tabStream = null;
    let micStream = null;

    if (mode !== "mic" && navigator.mediaDevices?.getDisplayMedia) {
      try {
        tabStream = await navigator.mediaDevices.getDisplayMedia({
          video: { width: { max: 16 }, height: { max: 16 }, frameRate: { max: 1 } },
          audio: { suppressLocalAudioPlayback: false, echoCancellation: false, noiseSuppression: false, autoGainControl: false },
          preferCurrentTab: false,
          selfBrowserSurface: "exclude",
          surfaceSwitching: "include",
          systemAudio: "exclude",
        });
        // Serve solo l'audio: fermiamo subito il video per non rallentare la scheda condivisa (Zoom/Meet).
        tabStream.getVideoTracks().forEach((t) => t.stop());
        if (!tabStream.getAudioTracks().length) {
          tabStream.getTracks().forEach((t) => t.stop());
          tabStream = null;
          warnings.push("La scheda selezionata non condivide l'audio (spunta \"Condividi audio della scheda\"): sento solo il tuo microfono.");
        }
      } catch (e) {
        warnings.push("Condivisione scheda annullata: sento solo il tuo microfono.");
      }
    } else if (mode !== "mic") {
      warnings.push("Questo browser non supporta la cattura audio della scheda: uso solo il microfono.");
    }

    if (mode !== "tab") {
      try {
        micStream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      } catch (e) {
        warnings.push("Microfono non consentito: la tua voce non verrà trascritta.");
      }
    }

    if (!tabStream && !micStream) {
      tabStream?.getTracks().forEach((t) => t.stop());
      setError("Nessuna sorgente audio disponibile. Consenti il microfono oppure condividi una scheda con audio.");
      return;
    }

    tabStreamRef.current = tabStream;
    micStreamRef.current = micStream;
    stoppingRef.current = false;
    const nCh = tabStream && micStream ? 2 : 1;
    channelsRef.current = nCh;
    setChannels(nCh);
    const ws = openSocket();
    const ctx = new AudioContext();
    ctxRef.current = ctx;
    await ctx.audioWorklet.addModule("/pcm-worklet.js");
    const node = new AudioWorkletNode(ctx, "pcm16-worklet", {
      channelCount: nCh,
      channelCountMode: "explicit",
      channelInterpretation: "discrete",
      processorOptions: { channels: nCh },
    });
    let frames = 0;
    node.port.onmessage = (e) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(e.data.buffer);
      if (++frames % 5 === 0) {
        const lv = e.data.levels || [0];
        setLevel(Math.max(...lv));
        if (nCh === 2) setChannelLevels({ mic: lv[0], tab: lv[1] });
        else setChannelLevels({ mic: micStream ? lv[0] : 0, tab: tabStream ? lv[0] : 0 });
      }
    };
    if (nCh === 2) {
      const merger = ctx.createChannelMerger(2);
      ctx.createMediaStreamSource(micStream).connect(merger, 0, 0);
      ctx.createMediaStreamSource(new MediaStream(tabStream.getAudioTracks())).connect(merger, 0, 1);
      merger.connect(node);
    } else if (tabStream) {
      ctx.createMediaStreamSource(new MediaStream(tabStream.getAudioTracks())).connect(node);
    } else {
      ctx.createMediaStreamSource(micStream).connect(node);
    }
    if (tabStream) {
      tabStream.getAudioTracks()[0].onended = () => {
        tabStreamRef.current = null;
        setSources((s) => ({ ...s, tab: false }));
        if (!micStreamRef.current) stop("La condivisione della scheda è stata interrotta.");
        else setError("Condivisione scheda interrotta: continuo con il solo microfono.");
      };
    }
    if (micStream) {
      micStream.getAudioTracks()[0].onended = () => {
        micStreamRef.current = null;
        setSources((s) => ({ ...s, mic: false }));
        if (!tabStreamRef.current) stop("Il microfono è stato disattivato.");
      };
    }
    nodeRef.current = node;
    setSources({ tab: !!tabStream, mic: !!micStream });
    if (warnings.length) setError(warnings.join(" "));
    return ws;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSocket]);

  const stop = useCallback((reason) => {
    stoppingRef.current = true;
    wsRef.current?.close();
    ctxRef.current?.close().catch(() => {});
    tabStreamRef.current?.getTracks().forEach((t) => t.stop());
    micStreamRef.current?.getTracks().forEach((t) => t.stop());
    wsRef.current = null;
    ctxRef.current = null;
    tabStreamRef.current = null;
    micStreamRef.current = null;
    setSources({ tab: false, mic: false });
    setChannelLevels({ mic: 0, tab: 0 });
    setLevel(0);
    setInterims({});
    setPendingTrigger(null);
    setDraft(null);
    suggestionIdRef.current = null;
    setStatus("ended");
    if (reason) setError(reason);
  }, []);

  const reconnect = useCallback(() => {
    if (!tabStreamRef.current && !micStreamRef.current) return;
    wsRef.current?.close();
    stoppingRef.current = false;
    openSocket();
  }, [openSocket]);

  const markSeller = useCallback((speaker) => {
    setSellerSpeaker(speaker);
    setSellerAuto(null);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "set_seller_speaker", speaker }));
    }
  }, []);

  useEffect(() => () => {
    stoppingRef.current = true;
    wsRef.current?.close();
    ctxRef.current?.close().catch(() => {});
    tabStreamRef.current?.getTracks().forEach((t) => t.stop());
    micStreamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  return { status, interim, interims, segments, suggestions, pendingTrigger, draft, coachState, sellerSpeaker, sellerAuto, voiceProfile, error, level, channelLevels, sources, channels, multichannel: channels === 2, start, stop, reconnect, markSeller, isCapturing: sources.tab || sources.mic };
};
